import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { FolderTree, Plus, Trash2, Pencil, X, Check } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function PortfolioCategoriesCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ name: "", description: "", sort_order: 0 });
  const [newDraft, setNewDraft] = useState({ name: "", description: "", sort_order: 0 });

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/portfolio-strategy/categories`, { headers: authHeaders() });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const handleAdd = async () => {
    const name = (newDraft.name || "").trim();
    if (!name) { toast.error("Category name required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/portfolio-strategy/categories`, newDraft, { headers: authHeaders() });
      toast.success(`Category "${name}" added`);
      setNewDraft({ name: "", description: "", sort_order: 0 });
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    } finally { setSaving(false); }
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setDraft({ name: it.name, description: it.description || "", sort_order: it.sort_order || 0 });
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/portfolio-strategy/categories/${editingId}`, draft, { headers: authHeaders() });
      toast.success("Updated");
      setEditingId(null);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  const handleDelete = async (it) => {
    if (!window.confirm(`Delete category "${it.name}"? This is blocked if any Model Portfolio still uses it.`)) return;
    try {
      await axios.delete(`${API_URL}/portfolio-strategy/categories/${it.id}`, { headers: authHeaders() });
      toast.success("Deleted");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Card className="border-emerald-200" data-testid="portfolio-categories-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg text-emerald-800">
          <FolderTree className="w-5 h-5" />
          Portfolio Categories
        </CardTitle>
        <p className="text-xs text-gray-500 mt-1">
          The canonical list of investment purposes — Wealth, Tax, Short Term, Thematic, Foreign Portfolio, Commodities, etc. Every Model Portfolio picks one. A scheme can belong to only ONE category at a time.
        </p>
      </CardHeader>
      <CardContent>
        {/* Add new */}
        <div className="grid grid-cols-12 gap-2 items-end mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-md">
          <div className="col-span-4">
            <label className="text-[11px] text-gray-700 font-medium">Name</label>
            <Input value={newDraft.name} onChange={(e) => setNewDraft({ ...newDraft, name: e.target.value })}
              placeholder="e.g. Wealth" data-testid="new-cat-name" />
          </div>
          <div className="col-span-5">
            <label className="text-[11px] text-gray-700 font-medium">Description</label>
            <Input value={newDraft.description} onChange={(e) => setNewDraft({ ...newDraft, description: e.target.value })}
              placeholder="Long-term wealth creation" data-testid="new-cat-desc" />
          </div>
          <div className="col-span-2">
            <label className="text-[11px] text-gray-700 font-medium">Sort</label>
            <Input type="number" value={newDraft.sort_order}
              onChange={(e) => setNewDraft({ ...newDraft, sort_order: Number(e.target.value) || 0 })}
              data-testid="new-cat-sort" />
          </div>
          <div className="col-span-1">
            <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 w-full"
              onClick={handleAdd} disabled={saving} data-testid="new-cat-add">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-sm text-gray-500 py-6 text-center">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-gray-500 py-6 text-center">No categories yet — add your first one above.</div>
        ) : (
          <div className="border border-gray-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-600">
                <tr>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-left px-3 py-2">Description</th>
                  <th className="text-right px-3 py-2 w-20">Sort</th>
                  <th className="w-32"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-t border-gray-100" data-testid={`cat-row-${it.name_lower}`}>
                    {editingId === it.id ? (
                      <>
                        <td className="px-3 py-2"><Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></td>
                        <td className="px-3 py-2"><Input value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></td>
                        <td className="px-3 py-2"><Input type="number" value={draft.sort_order} onChange={(e) => setDraft({ ...draft, sort_order: Number(e.target.value) || 0 })} className="text-right" /></td>
                        <td className="px-3 py-2 text-right">
                          <Button size="sm" variant="ghost" onClick={handleSaveEdit} disabled={saving} className="text-emerald-700"><Check className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}><X className="w-4 h-4" /></Button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-3 py-2 font-medium text-gray-800">{it.name}</td>
                        <td className="px-3 py-2 text-gray-600 text-xs">{it.description || <span className="text-gray-300">—</span>}</td>
                        <td className="px-3 py-2 text-right text-xs text-gray-500">{it.sort_order}</td>
                        <td className="px-3 py-2 text-right">
                          <Button size="sm" variant="ghost" onClick={() => startEdit(it)} data-testid={`cat-edit-${it.name_lower}`}><Pencil className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(it)} className="text-rose-600" data-testid={`cat-delete-${it.name_lower}`}><Trash2 className="w-4 h-4" /></Button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
